/**
 * "Unlinked documents (N)" — documents of a searched document corpus that
 * link to no drug (spec 2026-10-16 §3). ADMIN-ONLY until K4: KeywordSurface
 * renders it only when the admin "Search in" selection is active.
 *
 * Collapsed by default. "Load more" re-issues the executed request with the
 * next ``unlinked_offset`` (via ``onLoadMore``) and APPENDS the rows; a new
 * search (a new ``group`` object) starts over.
 */
import React, { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import type { UnlinkedDocument, UnlinkedDocumentGroup } from '../types';

/** Page size of the group (the backend default ``unlinked_limit``). */
export const UNLINKED_PAGE_SIZE = 20;

const SOURCE_LABEL: Record<string, string> = { fda_letter: 'Enforcement Letter' };

/** Where an unlinked row opens. Letters reuse ``LetterResultRow``'s
 *  drug-less target; a corpus without a drug-less viewer gets no link. */
export function unlinkedDocumentHref(doc: Pick<UnlinkedDocument, 'source' | 'document_id'>): string | null {
  if (doc.source === 'fda_letter') return `/fda-letter/${doc.document_id}`;
  return null;
}

function sanitize(html: string): string {
  return DOMPurify.sanitize(html, { ALLOWED_TAGS: ['mark', 'b'], ALLOWED_ATTR: [] });
}

interface Props {
  /** ``unlinked_documents`` of the executed response. */
  group: UnlinkedDocumentGroup | null | undefined;
  /** The group is null because a drug filter suppressed it. */
  hiddenByFilters: boolean;
  /** Fetch the group page at ``offset`` (same request otherwise). */
  onLoadMore: (offset: number) => Promise<UnlinkedDocumentGroup | null | undefined>;
}

const UnlinkedDocumentsSection: React.FC<Props> = ({ group, hiddenByFilters, onLoadMore }) => {
  const [open, setOpen] = useState(false);
  const [extra, setExtra] = useState<UnlinkedDocument[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => { setExtra([]); }, [group]);

  if (!group) {
    if (!hiddenByFilters) return null;
    return (
      <section className="rounded border border-line bg-white px-4 py-3 text-sm text-ink-3"
        data-testid="unlinked-documents">
        <span className="font-medium text-ink">Unlinked documents</span>
        {' — '}Hidden while drug filters are applied
      </section>
    );
  }

  const rows = [...group.results, ...extra];
  const more = rows.length < group.total_count;
  const loadMore = async () => {
    setLoading(true);
    try {
      const next = await onLoadMore(rows.length);
      if (next) setExtra((prev) => [...prev, ...next.results]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="rounded border border-line bg-white" data-testid="unlinked-documents">
      <button type="button" className="w-full px-4 py-3 text-left text-sm font-medium text-ink"
        aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {open ? '▾' : '▸'} Unlinked documents ({group.total_count})
      </button>
      {open && (
        <div className="border-t border-line-subtle px-4 py-3 space-y-3">
          {rows.length === 0 && <p className="text-sm text-ink-3">No unlinked documents match.</p>}
          {rows.map((doc) => {
            const href = unlinkedDocumentHref(doc);
            const title = doc.document_title || `Document ${doc.document_id}`;
            return (
              <article key={`${doc.source}:${doc.document_id}`} className="text-sm"
                data-testid="unlinked-document-row">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded border border-line px-1.5 text-[11px] text-ink-2">
                    {SOURCE_LABEL[doc.source] ?? doc.source}
                  </span>
                  {href
                    ? <a href={href} className="font-medium text-accent hover:underline">{title}</a>
                    : <span className="font-medium text-ink">{title}</span>}
                  {doc.document_date && <span className="text-ink-3">{doc.document_date}</span>}
                  <span className="text-ink-3">{doc.match_count} matches</span>
                </div>
                {doc.highlights.map((h) => (
                  <p key={h.chunk_id ?? h.section_id} className="mt-1 text-ink-2"
                    dangerouslySetInnerHTML={{ __html: sanitize(h.headline) }} />
                ))}
              </article>
            );
          })}
          {more && (
            <button type="button" onClick={() => { void loadMore(); }} disabled={loading}
              className="text-[13px] text-accent hover:text-accent-deep disabled:opacity-50">
              {loading ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      )}
    </section>
  );
};

export default UnlinkedDocumentsSection;
